// Persisted transcript of each chat turn; distinct from the turn-metadata
// table below it, which never carries the actual messages.

import { useSiteChats } from '../hooks';
import { QueryState } from '../../../components/QueryState';
import type { ChatsResponse, ChatTurn } from '../../../lib/types';

const ID = 'em-mgr--site--chats';

export interface ChatSession {
  sessionId: string;
  /** Newest-first turns, as served. */
  turns: ChatTurn[];
  /** Timestamp of the most recent turn in the session. */
  lastTs: string;
}

// Preserves server order; turns with no session id group under '' rather than
// being dropped.
export function groupBySession(turns: ChatTurn[]): ChatSession[] {
  const bySession = new Map<string, ChatSession>();
  for (const turn of turns) {
    const key = turn.sessionId ?? '';
    let session = bySession.get(key);
    if (!session) {
      session = { sessionId: key, turns: [], lastTs: turn.ts };
      bySession.set(key, session);
    }
    session.turns.push(turn);
    if (turn.ts > session.lastTs) session.lastTs = turn.ts;
  }
  return [...bySession.values()];
}

export interface ConversationsProps {
  siteId: string;
  /** Expanded session from the URL (?session=). Null = all collapsed. */
  session?: string | null;
  /** Navigate to a session. Omitted → local fallback state (unit tests). */
  onSessionChange?: (session: string | null) => void;
}

export function Conversations({
  siteId,
  session: routeSession = null,
  onSessionChange,
}: ConversationsProps) {
  const { data, isPending, isError, error } = useSiteChats(siteId);

  return (
    <QueryState<ChatsResponse>
      idBase={ID}
      isPending={isPending}
      isError={isError}
      error={error}
      data={data}
    >
      {(d) => {
        // em-manager present, em-editor-agent absent — distinct from "no chats yet".
        if (!d.available) {
          return (
            <div
              data-id={`${ID}--unavailable`}
              className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500 dark:border-slate-700"
            >
              The editor agent is not installed on this site.
            </div>
          );
        }
        if (d.turns.length === 0) {
          return (
            <div data-id={`${ID}--empty`} className="p-4 text-sm text-slate-500">
              No conversations recorded.
            </div>
          );
        }
        const sessions = groupBySession(d.turns);
        return (
          <div data-id={ID} className="space-y-2">
            {sessions.map((s) => (
              <SessionBlock
                key={s.sessionId}
                session={s}
                expanded={routeSession === s.sessionId}
                onToggle={() =>
                  onSessionChange?.(routeSession === s.sessionId ? null : s.sessionId)
                }
                routed={Boolean(onSessionChange)}
              />
            ))}
          </div>
        );
      }}
    </QueryState>
  );
}

function SessionBlock({
  session,
  expanded,
  onToggle,
  routed,
}: {
  session: ChatSession;
  expanded: boolean;
  onToggle: () => void;
  routed: boolean;
}) {
  // Unrouted use falls back to native details/summary so it still expands without a router (unit tests).
  const isOpen = routed ? expanded : undefined;
  return (
    <details
      data-id={`${ID}--session`}
      open={isOpen}
      className="rounded-lg border border-slate-200 dark:border-slate-800"
    >
      <summary
        data-id={`${ID}--session-summary`}
        onClick={routed ? (e) => { e.preventDefault(); onToggle(); } : undefined}
        className="cursor-pointer px-3 py-2 text-sm"
      >
        <span className="font-medium">{session.sessionId || '(no session id)'}</span>
        <span className="ml-2 text-slate-500">
          {session.turns.length} turn{session.turns.length === 1 ? '' : 's'} · {session.lastTs}
        </span>
      </summary>
      <div className="space-y-3 border-t border-slate-100 p-3 dark:border-slate-800">
        {session.turns.map((t) => (
          <TurnBlock key={t.id} turn={t} />
        ))}
      </div>
    </details>
  );
}

function TurnBlock({ turn }: { turn: ChatTurn }) {
  return (
    <div data-id={`${ID}--turn`} className="space-y-1 text-sm">
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <span>{turn.ts}</span>
        <span>{turn.wpUser}</span>
        {turn.model && <span>{turn.model}</span>}
        {turn.changed && (
          <span
            data-id={`${ID}--changed`}
            className="rounded bg-blue-100 px-1.5 py-0.5 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300"
          >
            changed
          </span>
        )}
        {turn.status && turn.status !== 'ok' && (
          <span
            data-id={`${ID}--status`}
            className="rounded bg-red-100 px-1.5 py-0.5 text-red-800 dark:bg-red-900/40 dark:text-red-300"
          >
            {turn.status}
          </span>
        )}
      </div>

      <p data-id={`${ID}--user`} className="whitespace-pre-wrap">
        <span className="font-medium text-slate-500">user: </span>
        {turn.userMessage}
      </p>
      <p data-id={`${ID}--assistant`} className="whitespace-pre-wrap">
        <span className="font-medium text-slate-500">agent: </span>
        {turn.assistantReply}
      </p>

      {turn.toolCalls.length > 0 && (
        <details data-id={`${ID}--tools`} className="text-xs text-slate-500">
          <summary className="cursor-pointer">
            {turn.toolCalls.length} tool call{turn.toolCalls.length === 1 ? '' : 's'}
          </summary>
          <ul className="ml-4 list-disc">
            {turn.toolCalls.map((c, i) => (
              <li key={i} data-id={`${ID}--tool`}>
                {c.name}
                {c.status && ` — ${c.status}`}
              </li>
            ))}
          </ul>
        </details>
      )}

      {turn.error && (
        <p data-id={`${ID}--error`} className="text-xs text-red-600 dark:text-red-400">
          {turn.error}
        </p>
      )}
    </div>
  );
}
